import React, { useEffect, useState } from 'react';
import { DatePicker, Form, Input, InputNumber, Modal } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../../utils/feedback';
import { formatCurrency } from '../../../utils/currency';
import { POLISH_MONEY_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import { enterCashBalance } from '../managementDashboard/liquidityApi';

// „Wpisz saldo” (Płynność): the cash on all company accounts on one day, typed
// by ADMIN. Every save is a new entry of the append-only history (backend
// cashBalanceService.js); a wrong amount is corrected by typing it again.

export const CASH_BALANCE_MAX_AMOUNT = 1000000000;
export const CASH_BALANCE_MAX_NOTE = 200;

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

interface FormValues {
  balanceDate: Dayjs;
  amount: number;
  note?: string;
}

const CashBalanceModal: React.FC<Props> = ({ open, onClose, onSaved }) => {
  const { t } = useTranslation(['reports', 'common']);
  const k = (key: string, options?: Record<string, unknown>) => t(`reports:liquidity.balanceModal.${key}`, options ?? {}) as string;
  const feedback = useFeedback();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);

  // Seed after the modal is open (antd mounts its form lazily).
  useEffect(() => {
    if (open) form.setFieldsValue({ balanceDate: dayjs(), amount: undefined, note: undefined });
  }, [open, form]);

  const submit = async (values: FormValues) => {
    setSaving(true);
    try {
      const balanceDate = values.balanceDate.format('YYYY-MM-DD');
      const saved = await enterCashBalance({ balanceDate, amount: values.amount, note: values.note?.trim() || null });
      feedback.success(k('saved', {
        amount: formatCurrency(saved?.amount ?? values.amount, 'PLN'),
        date: dayjs(balanceDate).format('DD.MM.YYYY'),
      }));
      onSaved();
      onClose();
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      feedback.error(message ? k('saveFailedReason', { reason: message }) : k('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={k('title')}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={k('save')}
      cancelText={t('common:cancel')}
      confirmLoading={saving}
      maskClosable={false}
      destroyOnHidden
      className="obj-drawer liquidity-balance-modal"
    >
      <p className="report-muted report-cut liquidity-balance-modal__lead" title={k('lead')}>{k('lead')}</p>
      <Form form={form} layout="vertical" onFinish={submit} requiredMark>
        <div className="liquidity-balance-modal__row">
          <Form.Item name="balanceDate" label={k('date')} rules={[{ required: true, message: k('dateRequired') }]}>
            <DatePicker
              format="DD.MM.YYYY"
              allowClear={false}
              disabledDate={(day) => day.isAfter(dayjs(), 'day')}
              className="liquidity-balance-modal__control"
              data-testid="cash-balance-date"
            />
          </Form.Item>
          <Form.Item
            name="amount"
            label={k('amount')}
            tooltip={k('amountHint')}
            rules={[
              { required: true, message: k('amountRequired') },
              {
                type: 'number',
                min: -CASH_BALANCE_MAX_AMOUNT,
                max: CASH_BALANCE_MAX_AMOUNT,
                message: k('amountRange', { max: formatCurrency(CASH_BALANCE_MAX_AMOUNT, 'PLN') }),
              },
            ]}
          >
            <InputNumber
              className="liquidity-balance-modal__control"
              step={0.01}
              placeholder="0,00"
              precision={2}
              {...POLISH_MONEY_INPUT_NUMBER_PROPS}
              data-testid="cash-balance-amount"
            />
          </Form.Item>
        </div>
        <Form.Item name="note" label={k('note')} rules={[{ max: CASH_BALANCE_MAX_NOTE, message: k('noteTooLong', { max: CASH_BALANCE_MAX_NOTE }) }]}>
          <Input placeholder={k('notePlaceholder')} data-testid="cash-balance-note" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CashBalanceModal;
